import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import crossLogo from "@/images/cross-logo.svg";
import fileLogo from "@/images/file-logo.svg";
import { LazoFile } from "@/models/LazoFile";
import LazoFileService from "@/services/LazoFileService";
import { anyOfType, extractExtensionRemoveDot } from "@/utils/FilenameUtils";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { CSSProperties, Dispatch, SetStateAction, useEffect, useState } from "react";
import { fileSizeToHumanReadable } from "../data-room/FileDisplayTable";
import { LazoFileViewerModal } from "../task-components/LazoFileViewerModal";
import { DownloadIcon } from "../utils/icons/DownloadIcon";

function getFileTypeMeta(filename: string): { label: string; color: string; bg: string } {
  const ext = extractExtensionRemoveDot(filename).toLowerCase();
  if (ext === "pdf") return { label: "PDF", color: "#C62828", bg: "#FFEBEE" };
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return { label: "IMG", color: "#00695C", bg: "#E0F2F1" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", color: "#2E7D32", bg: "#E8F5E9" };
  if (["doc", "docx"].includes(ext)) return { label: "DOC", color: "#1565C0", bg: "#E3F2FD" };
  if (["zip", "rar", "7z"].includes(ext)) return { label: "ZIP", color: "#E65100", bg: "#FFF3E0" };
  const label = ext.toUpperCase();
  return { label: label.length <= 4 ? label || "FILE" : "FILE", color: "#424242", bg: "#F5F5F5" };
}

export function ImagePreviewFile(props: { url: string; fname: string; loadErrorSrc: string; previewStyle: CSSProperties }) {
  const [openModal, setOpenModal] = useState<boolean>(false);

  function onLoadError(ev: React.SyntheticEvent<HTMLImageElement, Event>) {
    ev.currentTarget.src = props.loadErrorSrc;
  }

  return (
    <>
      <img
        onClick={() => setOpenModal(true)}
        onError={onLoadError}
        src={props.url || props.loadErrorSrc}
        alt={props.fname}
        style={{
          width: "32px",
          objectFit: "cover",
          cursor: "pointer",
          ...(props.url && props.previewStyle),
        }}
      />
      {openModal && (
        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          style={{ display: "flex", width: "100vw", height: "100vh", alignItems: "center", justifyContent: "center" }}
        >
          <img
            onError={onLoadError}
            src={props.url || props.loadErrorSrc}
            alt={props.fname}
            style={{ maxHeight: "90vh", maxWidth: "90vw" }}
          />
        </Modal>
      )}
    </>
  );
}

export function PdfPreviewFile(props: { fetchUrl: () => Promise<string> }) {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  async function handleOpen() {
    setLoading(true);
    try {
      const url = await props.fetchUrl();
      setPdfUrl(url);
      setOpenModal(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div
        onClick={handleOpen}
        style={{ width: "100%", height: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {loading ? (
          <Spinner size="1rem" sx={{ color: Colors.BLACK }} />
        ) : (
          <img src={fileLogo.src} alt="pdf" style={{ width: "32px", objectFit: "cover" }} />
        )}
      </div>
      {openModal && pdfUrl && (
        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          style={{ display: "flex", width: "100vw", height: "100vh", alignItems: "center", justifyContent: "center" }}
        >
          <div
            style={{
              display: "flex",
              width: "90vw",
              height: "90vh",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "24px",
              overflow: "hidden",
            }}
          >
            <Viewer fileUrl={pdfUrl} />
          </div>
        </Modal>
      )}
    </>
  );
}

export function BasicFile() {
  return <img src={fileLogo.src} alt={"basic-file"} style={{ width: "32px", objectFit: "cover" }} />;
}

type Props = {
  file: File | LazoFile;
  idx?: number;
  setFiles?: Dispatch<SetStateAction<File[]>>;
  hideDelete?: boolean;
  lazoFile?: LazoFile;
  onDownload?: (f?: LazoFile) => Promise<void>;
  containerStyle?: CSSProperties;
  textStyle?: CSSProperties;
  filePreviewContainerStyle?: CSSProperties;
  filePreviewStyle?: CSSProperties;
  showPreview?: boolean;
};

const IMG_EXTENSIONS = ["png", "jpg", "jpeg"];

export function FileContainer(props: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!(props.file instanceof File)) {
      if (!anyOfType(props.file.name, [...IMG_EXTENSIONS, "pdf"])) return;
      LazoFileService.generatePreSignedUrl(props.file.externalId).then((r) => {
        r.onSuccess((ps) => setObjectUrl(ps));
      });
    } else {
      setObjectUrl(URL.createObjectURL(props.file));
    }

    return () => {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    };
  }, []);

  function deleteAndCleanup(): void {
    URL.revokeObjectURL(objectUrl);
    setObjectUrl(null);
    props.setFiles?.((prev) => prev.filter((f) => f !== props.file));
  }

  async function handleDownload() {
    if (!props.lazoFile) return;
    setDownloading(true);
    try {
      await LazoFileService.downloadFile(props.lazoFile.externalId, props.lazoFile.name);
    } finally {
      setDownloading(false);
    }
  }

  const isPdfFile = (props.file instanceof File && props.file?.type?.includes("pdf")) || props.file.name.includes(".pdf");
  const isLazoFile = !!props.lazoFile;
  const meta = getFileTypeMeta(props.file.name);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem 0.625rem",
          backgroundColor: Colors.WHITE,
          borderRadius: "0.75rem",
          border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
          width: "14rem",
          boxSizing: "border-box",
          ...props.containerStyle,
        }}
      >
        {/* File type badge */}
        <Box
          sx={{
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "0.5rem",
            backgroundColor: meta.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {props.showPreview && !isLazoFile && !isPdfFile ? (
            <ImagePreviewFile url={objectUrl} fname={props.file.name} loadErrorSrc={fileLogo.src} previewStyle={props.filePreviewStyle} />
          ) : (
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                color: meta.color,
                letterSpacing: "0.04em",
                lineHeight: 1,
              }}
            >
              {meta.label}
            </Typography>
          )}
        </Box>

        {/* Name + size */}
        <Tooltip followCursor title={props.file.name}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: Colors.ELEGANT_BLACK,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...props.textStyle,
              }}
            >
              {props.file.name}
            </Typography>
            <Typography sx={{ fontSize: "0.6875rem", color: Colors.LIGHT_GRAY_7A7E82 }}>
              {fileSizeToHumanReadable(props.file.size)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.125rem", flexShrink: 0 }}>
          {isLazoFile && (
            <>
              <Tooltip title="View">
                <IconButton size="small" onClick={() => setViewerOpen(true)}>
                  <RemoveRedEyeOutlinedIcon sx={{ fontSize: "1rem", color: Colors.LIGHT_GRAY_8A8E93 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton size="small" onClick={handleDownload} disabled={downloading}>
                  {downloading ? (
                    <Spinner size="0.875rem" sx={{ color: Colors.LIGHT_GRAY_8A8E93 }} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: "0.875rem", color: Colors.LIGHT_GRAY_8A8E93 }} />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
          {!props.hideDelete && (
            <IconButton size="small" onClick={deleteAndCleanup}>
              <img src={crossLogo.src} alt="Remove" style={{ width: "14px", height: "14px" }} />
            </IconButton>
          )}
        </Box>
      </div>

      {isLazoFile && <LazoFileViewerModal open={viewerOpen} onClose={() => setViewerOpen(false)} file={props.lazoFile} />}
    </>
  );
}
